import * as THREE from 'three';
import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  compute3DMeshDataAndPosition,
  get3DMeshDataAndPosition,
  loadTexture,
  replaceGeometryAttributesOnGeometry,
  makeMeshWithData,
} from '../../utils';
import {
  createReactThreeGeometry,
  createReactThreeMaterial,
} from 'containers/Viewer/reactThreeUtils';
import { getMaterialData } from '../../helpers';
import { useSpring, a } from 'react-spring/three';
import ThreeBSP from '../../ThreeCSG';
import * as constantsViewer from '../../constants';
import textureMirror from 'assets/images/textures/mirror2.jpg';
import textureAlu from 'assets/images/textures/alu-inox.jpg';
import textureWhiteBeech from 'assets/images/textures/whitebeech.jpg';
import textureMdfNatural from 'assets/images/textures/mdfnatural2.jpg';
import textureGlass from 'assets/images/textures/glass2.jpg';
import data from './data/pane';

export function DoorFrontPane({
  door,
  dPosition,
  shelfHeight,
  cabinetWidth,
  cabinetDepth,
  unselected = false,
}) {
  const mesh = useRef();

  const [prevSizeData, setPrevSizeData] = useState({
    cabinetWidth: cabinetWidth,
    shelfHeight: shelfHeight,
  });

  const textures = useMemo(
    () => ({
      textureWhiteBeechLoaded: loadTexture(textureWhiteBeech),
      textureMdfNaturalLoaded: loadTexture(textureMdfNatural),
      textureMirrorLoaded: loadTexture(textureMirror),
      textureAluLoaded: loadTexture(textureAlu),
      textureGlassLoaded: loadTexture(textureGlass),
    }),
    [
      textureWhiteBeech,
      textureMdfNatural,
      textureMirror,
      textureAlu,
      textureGlass,
    ],
  );

  let holeSize = door.base.hole;
  if (door.doorHandleType === 'G') holeSize = 0;
  if (door.doorHandleType === 'C') holeSize = 0;

  let zOffset = 15;
  if (!door.doorIsInside) zOffset = 9;

  const baseDataModel = useMemo(() => compute3DMeshDataAndPosition(data), [
    data,
  ]);
  const baseDataModelRealSize = { x: 488.5, y: 380, z: 2 };

  const baseData = useMemo(
    () =>
      get3DMeshDataAndPosition(
        generateGeometryFrontPaneThree(
          dPosition,
          baseDataModel.size,
          prevSizeData.shelfHeight,
          prevSizeData.cabinetWidth,
          holeSize,
        ),
      ),
    [data],
  );

  const [spring, set] = useSpring(() => ({
    position: getPosition(cabinetWidth, cabinetDepth, zOffset, dPosition),
    scale: [1, 1, 1],
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  const materialData = getMaterialData(door, textures);

  useEffect(() => {
    let self = mesh.current;
    if (self)
      changeGeometryWithHole(
        self,
        dPosition,
        prevSizeData.shelfHeight,
        baseDataModelRealSize,
        baseDataModel.size,
        prevSizeData.cabinetWidth,
        holeSize,
      );
  }, [holeSize]);

  useEffect(() => {
    let self = mesh.current;
    if (
      self &&
      (cabinetWidth != prevSizeData.cabinetWidth ||
        shelfHeight != prevSizeData.shelfHeight)
    ) {
      self.geometry.computeBoundingBox();
      let size = new THREE.Vector3();
      self.geometry.boundingBox.getSize(size);

      if (holeSize > 0) {
        // Change geometry to remove hole (null for hole) on old size values,
        // only if animations are active
        if (constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE)
          changeGeometryWithHole(
            self,
            dPosition,
            prevSizeData.shelfHeight,
            baseDataModelRealSize,
            baseDataModel.size,
            prevSizeData.cabinetWidth,
            0,
          );

        // Animate scaling and after update geometry to add hole
        set({
          scale: getScale(size, cabinetWidth, shelfHeight),
          position: getPosition(cabinetWidth, cabinetDepth, zOffset, dPosition),
          onRest: () => {
            changeGeometryWithHole(
              self,
              dPosition,
              shelfHeight,
              baseDataModelRealSize,
              baseDataModel.size,
              cabinetWidth,
              holeSize,
            );
          },
        });
      } else {
        set({
          scale: getScale(size, cabinetWidth, shelfHeight),
          position: getPosition(cabinetWidth, cabinetDepth, zOffset, dPosition),
          onRest: null,
        });
      }

      setPrevSizeData({
        cabinetWidth: cabinetWidth,
        shelfHeight: shelfHeight,
      });
    }
  }, [cabinetWidth, shelfHeight]);

  useEffect(() => {
    let self = mesh.current;
    if (self)
      set({
        position: getPosition(cabinetWidth, cabinetDepth, zOffset, dPosition),
        onRest: null,
      });
  }, [cabinetDepth]);

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      position={spring.position}
      scale={spring.scale}
      renderOrder={constantsViewer.ZORDER_DOORS}
    >
      {createReactThreeGeometry(baseData)}
      {createReactThreeMaterial(materialData, unselected)}
    </a.mesh>
  );
}

export function DoorHoleShadow({
  door,
  dPosition,
  shelfHeight,
  cabinetWidth,
  cabinetDepth,
  unselected = false,
}) {
  const mesh = useRef();

  let holeSize = door.base.hole;
  if (door.doorHandleType === 'G') holeSize = 0;

  const [prevHoleSize, setPrevHoleSize] = useState(holeSize);

  let zOffset = 15;
  if (!door.doorIsInside) zOffset = 9;

  // Set shadow balow
  zOffset += 2.5;

  const baseData = useMemo(
    () =>
      get3DMeshDataAndPosition(
        generateGeometryHoleShadowThree(dPosition, cabinetWidth, holeSize),
      ),
    [data],
  );

  const [spring, set] = useSpring(() => ({
    position: getPositionHoleShadow(
      cabinetWidth,
      cabinetDepth,
      zOffset,
      dPosition,
      holeSize,
    ),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = mesh.current;
    if (self) {
      if (prevHoleSize != holeSize) {
        changeGeometryHoleShadow(self, dPosition, cabinetWidth, holeSize);

        setPrevHoleSize(holeSize);
      }

      set({
        position: getPositionHoleShadow(
          cabinetWidth,
          cabinetDepth,
          zOffset,
          dPosition,
          holeSize,
        ),
      });
    }
  }, [cabinetWidth, shelfHeight, holeSize]);

  useEffect(() => {
    let self = mesh.current;
    if (self)
      set({
        position: getPositionHoleShadow(
          cabinetWidth,
          cabinetDepth,
          zOffset,
          dPosition,
          holeSize,
        ),
      });
  }, [cabinetDepth]);

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      renderOrder={constantsViewer.ZORDER_DOOR_HOLE_SHADOW}
      position={spring.position}
    >
      {createReactThreeGeometry(baseData)}
      <meshPhongMaterial
        attach="material"
        color={0x000000}
        specular={0x919191}
        shininess={4.92458}
        opacity={unselected ? 0 : 0.2}
        transparent
      />
    </a.mesh>
  );
}

function generateGeometryFrontPaneThree(
  dPosition,
  size,
  shelfHeight,
  cabinetWidth,
  holeSize,
) {
  const hole = holeSize;

  let scale = getScale(size, cabinetWidth, shelfHeight);
  let scaleX = scale[0];
  let scaleY = scale[1];

  var paneMesh = makeMeshWithData(data);

  // ThreeCSG does not support adding texture on Mesh with BufferGeometry, so need to
  // convert to Geometry (no impact on rendering performance due to final export in BufferGeometry)
  var geometry = new THREE.Geometry().fromBufferGeometry(paneMesh.geometry);
  paneMesh = new THREE.Mesh(geometry, paneMesh.material);

  var center = new THREE.Vector3();
  paneMesh.geometry.computeBoundingBox();
  paneMesh.geometry.boundingBox.getCenter(center);
  paneMesh.geometry.center();
  paneMesh.position.copy(center);

  const cylinderRadius = hole / 2;
  var geometryCylinder = new THREE.CylinderGeometry(
    cylinderRadius,
    cylinderRadius,
    40,
    32,
  );
  var materialCylinder = new THREE.MeshBasicMaterial({ color: 0x000000 });
  var cylinder = new THREE.Mesh(geometryCylinder, materialCylinder);
  cylinder.rotation.set(0, Math.PI / 2, Math.PI / 2);

  var holeOffset = 40;
  if (hole === 4) holeOffset = 50;

  paneMesh.geometry.scale(scaleX, scaleY, 1);
  /* paneMesh.scale.set(
    ((999 * (cabinetWidth / 999) - 31) / 2 + 4) / 968,
    (370 * (shelfHeight / 370)) / 370,
    1,
  );*/
  switch (dPosition) {
    case 'FL':
      paneMesh.position.set(-(999 * (cabinetWidth / 999) - 31) / 2 / 2, 0, 0);

      cylinder.position.set(
        -(999 * (cabinetWidth / 999) - 31) / 2 + cylinderRadius + holeOffset,
        0,
        0,
      );
      break;

    case 'FR':
      paneMesh.position.set((999 * (cabinetWidth / 999) - 31) / 2 / 2, 0, 0);

      cylinder.position.set(
        (999 * (cabinetWidth / 999) - 31) / 2 - cylinderRadius - holeOffset,
        0,
        0,
      );
      break;

    default:
      break;
  }

  var bsp1 = new ThreeBSP(paneMesh);
  var bsp2 = new ThreeBSP(cylinder);

  if (hole === 0) return bsp1.toBufferGeometry();

  return bsp1.subtract(bsp2).toBufferGeometry();
}

function generateGeometryHoleShadowThree(dPosition, cabinetWidth, holeSize) {
  let hole = holeSize;
  const cylinderRadius = hole / 2;
  var geometryCylinder = new THREE.CylinderGeometry(
    cylinderRadius,
    cylinderRadius,
    2,
    32,
  );
  var materialCylinder = new THREE.MeshBasicMaterial({ color: 0x000000 });
  var cylinder = new THREE.Mesh(geometryCylinder, materialCylinder);
  cylinder.rotation.set(0, Math.PI / 2, Math.PI / 2);

  var holeOffset = 40;
  if (hole === 4) holeOffset = 50;

  switch (dPosition) {
    case 'FL':
      cylinder.position.set(
        -(999 * (cabinetWidth / 999) - 31) / 2 + cylinderRadius + holeOffset,
        0,
        0,
      );
      break;

    case 'FR':
      cylinder.position.set(
        (999 * (cabinetWidth / 999) - 31) / 2 - cylinderRadius - holeOffset,
        0,
        0,
      );
      break;

    default:
      break;
  }

  var bsp = new ThreeBSP(cylinder);

  return bsp.toBufferGeometry();
}

/**
 * Get scale multiplicator from a door size to a goal door size
 */
function getScale(size, cabinetWidth, shelfHeight) {
  let spaceWidthNoScale = 23;
  let spaceHeightNoScale = 0;

  return [
    (cabinetWidth - spaceWidthNoScale) / 2 / size.x,
    (shelfHeight - spaceHeightNoScale) / size.y,
    1,
  ];
}

function getPosition(cabinetWidth, cabinetDepth, zOffset, dPosition) {
  switch (dPosition) {
    case 'FL':
      return [-(cabinetWidth - 31) / 2 / 2, 0, cabinetDepth / 2 - zOffset];
    case 'FR':
      return [(cabinetWidth - 31) / 2 / 2, 0, cabinetDepth / 2 - zOffset];
  }
}

function getPositionHoleShadow(
  cabinetWidth,
  cabinetDepth,
  zOffset,
  dPosition,
  holeSize,
) {
  let holeOffset = 40;
  if (holeSize === 4) holeOffset = 50;
  switch (dPosition) {
    case 'FL':
      return [
        -(cabinetWidth - 31) / 2 + holeSize / 2 + holeOffset,
        0,
        cabinetDepth / 2 - zOffset,
      ];
    case 'FR':
      return [
        (cabinetWidth - 31) / 2 - holeSize / 2 - holeOffset,
        0,
        cabinetDepth / 2 - zOffset,
      ];
  }
}

function changeGeometryWithHole(
  self,
  dPosition,
  shelfHeight,
  baseSize,
  size,
  cabinetWidth,
  holeSize,
) {
  let newData = get3DMeshDataAndPosition(
    generateGeometryFrontPaneThree(
      dPosition,
      size,
      shelfHeight,
      cabinetWidth,
      holeSize,
    ),
  );

  replaceGeometryAttributesOnGeometry(self.geometry, newData);

  // Revert scaling on geometry because mesh is already scaled
  // and cannott change scale on mesh beacause that causes visible bad change of scale
  self.geometry.scale(1 / self.scale.x, 1 / self.scale.y, 1);
}

function changeGeometryHoleShadow(self, dPosition, cabinetWidth, holeSize) {
  let newData = get3DMeshDataAndPosition(
    generateGeometryHoleShadowThree(dPosition, cabinetWidth, holeSize),
  );

  replaceGeometryAttributesOnGeometry(self.geometry, newData);
}
